/**
 * InlineSignaturePad — slim canvas signature input for embedding in forms.
 *
 * Unlike the modal-style `SignaturePad`, this version has no internal buttons,
 * no name input — it emits the current PNG data URL via onChange after each
 * stroke. The parent controls the layout, validation and submit flow.
 *
 * Props:
 *  - onChange(dataUrl|null)  → fired after each stroke end + on clear
 *  - height                   → canvas height in px (default 130)
 *  - testId                   → data-testid prefix (default "inline-sig")
 *  - disabled                 → read-only flag
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

export function InlineSignaturePad({
  onChange,
  height = 130,
  testId = "inline-sig",
  disabled = false,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  const resize = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const parent = cv.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr;
    cv.height = height * dpr;
    cv.style.width = `${w}px`;
    cv.style.height = `${height}px`;
    const ctx = cv.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0a4d24";
    ctx.lineWidth = 2.2;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, height);
  }, [height]);

  useEffect(() => {
    resize();
    const r = () => resize();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, [resize]);

  const getPos = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (onChange && canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    resize();
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="rounded-lg border-2 border-dashed border-zinc-700 bg-white overflow-hidden"
           style={{ height, touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          data-testid={`${testId}-canvas`}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          style={{ display: "block", cursor: disabled ? "not-allowed" : "crosshair" }}
        />
      </div>
      <div className="flex items-center justify-between text-[12px] text-zinc-300">
        <span>{hasInk
          ? "✓ Unterschrift erfasst"
          : "Hier mit Finger oder Maus unterschreiben"}</span>
        {hasInk && !disabled && (
          <button type="button" onClick={clear} data-testid={`${testId}-clear`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded hover:bg-zinc-800 text-zinc-400">
            <Eraser size={11} /> Löschen
          </button>
        )}
      </div>
    </div>
  );
}
